import type {
  ProfileAssignedPermissions,
  TechnicianPrivilegeModule,
} from '../../api/viewProfileApiService';
import {
  tPermission,
  initialPermissionsData,
  PERMISSION_KEY_MAP,
} from '@/epics/clientManagement/hooks/usePermission';
import { TECHNICIAN_LABELS } from '@/epics/technicianManagement/constants';

// ============================================================================
// Technician label helpers
// ============================================================================

const TECH_MODULE_LABEL: Record<string, string> = {
  manage_assigned_tasks: TECHNICIAN_LABELS.MODULE_MANAGE_TASKS,
  certificate_management: TECHNICIAN_LABELS.MODULE_CERTIFICATES,
  invoice_management: TECHNICIAN_LABELS.MODULE_INVOICES,
};

const TECH_PERM_LABEL: Record<string, string> = {
  taskList: TECHNICIAN_LABELS.PRIV_TASK_LIST,
  taskView: TECHNICIAN_LABELS.PRIV_TASK_VIEW,
  taskUpdateStatus: TECHNICIAN_LABELS.PRIV_TASK_UPDATE_STATUS,
  taskFillForm: TECHNICIAN_LABELS.PRIV_TASK_FILL_FORM,
  taskAcceptReject: TECHNICIAN_LABELS.PRIV_TASK_ACCEPT_REJECT,
  certificateList: TECHNICIAN_LABELS.PRIV_CERT_LIST,
  certificateView: TECHNICIAN_LABELS.PRIV_CERT_VIEW,
  certificateDownload: TECHNICIAN_LABELS.PRIV_CERT_DOWNLOAD,
  invoiceList: TECHNICIAN_LABELS.PRIV_INVOICE_LIST,
  invoiceView: TECHNICIAN_LABELS.PRIV_INVOICE_VIEW,
  invoiceDownload: TECHNICIAN_LABELS.PRIV_INVOICE_DOWNLOAD,
};

// ============================================================================
// Shared render shape
// ============================================================================

interface RenderedModule {
  key: string;
  title: string;
  chips: { value: string; label: string }[];
}

// ============================================================================
// Component
// ============================================================================

interface ProfilePermissionsProps {
  assignedPermissions?: ProfileAssignedPermissions | null;
  technicianPrivileges?: TechnicianPrivilegeModule[] | null;
}

const ProfilePermissions = ({
  assignedPermissions,
  technicianPrivileges,
}: ProfilePermissionsProps) => {
  let modulesWithChips: RenderedModule[] = [];

  if (technicianPrivileges) {
    modulesWithChips = technicianPrivileges
      .map((module) => {
        const chips: { value: string; label: string }[] = [];

        for (const fp of module.fixedPermissions ?? []) {
          chips.push({
            value: fp.key,
            label: TECH_PERM_LABEL[fp.key] ?? fp.name,
          });
        }

        for (const p of module.permissions) {
          if (p.allowed) {
            chips.push({
              value: p.key,
              label: TECH_PERM_LABEL[p.key] ?? p.name,
            });
          }
        }

        return {
          key: module.key,
          title: TECH_MODULE_LABEL[module.key] ?? module.name,
          chips,
        };
      })
      .filter((module) => module.chips.length > 0);
  } else {
    // Client path: build allowedMap from assignedPermissions
    const allowedMap: Record<string, boolean> = {};
    if (assignedPermissions?.privileges) {
      for (const privilege of assignedPermissions.privileges) {
        for (const perm of privilege.permissions) {
          allowedMap[perm.key] = perm.allowed;
        }
      }
    }

    const permissions = initialPermissionsData.map((module) => ({
      ...module,
      options: module.options.map((opt) => {
        if (opt.disabled) {
          return opt;
        }
        const backendKey = PERMISSION_KEY_MAP[opt.value];
        if (!backendKey) {
          return opt;
        }
        return { ...opt, checked: allowedMap[backendKey] ?? false };
      }),
    }));

    modulesWithChips = permissions
      .map((module) => ({
        key: module.title,
        title: tPermission(module.title),
        chips: module.options
          .filter((o) => o.checked)
          .map((o) => ({ value: o.value, label: tPermission(o.label) })),
      }))
      .filter((module) => module.chips.length > 0);
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 border border-[#DCDDE0] rounded-[8px] p-4">
        <p
          className="text-titleDefault font-medium text-on-surface-variant"
          data-testid="profile-permissions-title"
        >
          Uprawnienia
        </p>

        {modulesWithChips.length === 0 ? (
          <p
            className="text-bodyMedium text-on-surface-variant"
            data-testid="profile-permissions-empty"
          >
            Brak przypisanych uprawnień.
          </p>
        ) : (
          <div className="flex flex-col gap-8">
            {modulesWithChips.map((module) => (
              <div key={module.key} className="flex flex-col gap-3">
                <p className="text-bodySmall font-normal text-on-surface-variant">
                  {module.title}
                </p>
                <div className="flex flex-wrap gap-2">
                  {module.chips.map((chip) => (
                    <span
                      key={chip.value}
                      className="inline-flex items-center justify-center h-8 px-3 rounded-[8px] bg-information-90 text-information-30 text-labelLarge"
                      data-testid={`profile-permission-${chip.value}`}
                    >
                      {chip.label}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePermissions;
